/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import moment from 'moment';
import GetIndentTransfer from '../../hooks/fetch-hooks/get-indent-transfer/get-indent-transfer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faRefresh } from '@fortawesome/free-solid-svg-icons';
import DownloadMedicineList from '../../hooks/fetch-hooks/download-medicines/download-medicine-list';

const IndentTransferCard = ({ quotationId, onCreateIndent }) => {
    const { data: indentResponse, isLoading, isFetched, refetch } = GetIndentTransfer(quotationId);
    const [expandedIndentIndex, setExpandedIndentIndex] = useState(null); // Track which indent is expanded
    const [isRefreshing, setIsRefreshing] = useState(false);
    const { mutate: downloadMedicineList, isLoading: isDownloading } = DownloadMedicineList(quotationId);

    const indentDetails = indentResponse?.data?.data?.indentDetails || [];

    console.log("indentDetails", indentDetails);

    // Status badge colors
    const getStatusColor = (status) => {
        switch (status) {
            case 'SENT':
                return 'bg-green-200 text-green-900 border border-green-400';
            case 'FAILED':
            case 'CANCELLED':
                return 'bg-red-200 text-red-900 border border-red-400';
            default:
                return 'bg-gray-200 text-gray-900 border border-gray-400';
        }
    };

    // Item status icons
    const getItemStatusIcon = (status) => {
        switch (status) {
            case 'SENT':
                return '✓';
            case 'FAILED':
                return '❌';
            default:
                return '⏳';
        }
    };

    // Format store names
    const formatStoreName = (store) => {
        const storeNames = {
            easydoseStore: 'Easydose',
            opPharmacy: 'OP Pharmacy'
        };
        return storeNames[store] || store;
    };

    // Handle refresh
    const handleRefresh = async (e) => {
        e.stopPropagation();
        setIsRefreshing(true);
        await refetch();
        setIsRefreshing(false);
    };

    // Loading state
    if (isLoading && !isFetched) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-3 mt-4">
                <div className="text-sm text-gray-500">Loading indent transfer...</div>
            </div>
        );
    }

    // State 1: No indent created
    if (indentDetails.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-md border-2 border-gray-300 mt-4">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">Indent Transfer Status</h3>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                downloadMedicineList();
                            }}
                            disabled={isDownloading}
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5 shadow-sm whitespace-nowrap"
                        >
                            {isDownloading ? 'Downloading...' : 'Download medicine list'}
                        </button>
                        {onCreateIndent && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCreateIndent();
                                }}
                                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 flex items-center gap-1.5 shadow-sm whitespace-nowrap"
                            >
                                <span>+</span> Create Manual Indent
                            </button>
                        )}
                    </div>
                </div>
                <div className="p-4">
                    <span className="text-sm text-gray-500">No indent transfer created for this quotation</span>
                </div>
            </div>
        );
    }

    // State 2: One or more indents exist
    return (
        <div className="bg-white rounded-lg shadow-md border-2 border-gray-300 mt-4">
            {/* Heading */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">
                    Indent Transfer Status {indentDetails.length > 1 && `(${indentDetails.length} indents)`}
                </h3>
                <div className="flex items-center gap-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            downloadMedicineList();
                        }}
                        disabled={isDownloading}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5 shadow-sm whitespace-nowrap"
                    >
                        {isDownloading ? 'Downloading...' : 'Download medicine list'}
                    </button>
                    {onCreateIndent && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onCreateIndent();
                            }}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 flex items-center gap-1.5 shadow-sm whitespace-nowrap"
                        >
                            <span>+</span> Create Manual Indent
                        </button>
                    )}
                </div>
            </div>

            {/* List of all indents */}
            <div className="divide-y divide-gray-200">
                {indentDetails.map((indentData, indentIndex) => {
                    const statusColor = getStatusColor(indentData.status);
                    const items = indentData.items || [];
                    const itemsCount = items.length;
                    const isExpanded = expandedIndentIndex === indentIndex;

                    return (
                        <div key={indentData.indentNo || indentIndex} className="last:border-b-0">
                            {/* Collapsed Row View */}
                            <div
                                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() => setExpandedIndentIndex(isExpanded ? null : indentIndex)}
                            >
                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                    {/* Status Badge */}
                                    <span className={`px-3 py-1.5 rounded-md text-sm font-bold whitespace-nowrap shadow-sm ${statusColor}`}>
                                        {indentData.status === 'IN_TRANSIT' ? '⏳ IN TRANSIT' :
                                            indentData.status === 'COMPLETED' ? '✓ COMPLETED' :
                                                indentData.status === 'PENDING' ? '⏳ PENDING' :
                                                    indentData.status === 'SENT' ? '📤 SENT' :
                                                        indentData.status}
                                    </span>

                                    {/* ID */}
                                    <span className="text-sm font-bold text-gray-800 truncate">
                                        {indentData.indentNo || 'N/A'}
                                    </span>

                                    {/* Transfer Direction */}
                                    <span className="text-sm font-semibold text-gray-700 hidden sm:inline">
                                        {formatStoreName(indentData.transferFrom)} → {formatStoreName(indentData.transferTo)}
                                    </span>

                                    {/* Items Count */}
                                    <span className="text-sm font-semibold text-gray-700">
                                        {itemsCount} item{itemsCount !== 1 ? 's' : ''}
                                    </span>

                                    {/* Created Date */}
                                    {indentData.createdAt && (
                                        <span className="text-sm font-medium text-gray-600 hidden md:inline">
                                            {moment(indentData.createdAt).format('DD MMM, hh:mm A')}
                                        </span>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-3">
                                    <button
                                        disabled={isRefreshing}
                                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 rounded transition-colors"
                                        title="Refresh"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRefresh(e);
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faRefresh}
                                            spin={isRefreshing}
                                            className="w-4 h-4"
                                        />
                                    </button>
                                    <FontAwesomeIcon
                                        icon={isExpanded ? faChevronUp : faChevronDown}
                                        className="w-5 h-5 text-gray-500"
                                    />
                                </div>
                            </div>

                            {/* Expanded Details */}
                            {isExpanded && (
                                <div className="px-3 pb-3 pt-2 border-t border-gray-100 space-y-3 bg-gray-50">
                                    {/* Details Grid */}
                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                        <div>
                                            <label className="text-gray-500 font-medium">ID</label>
                                            <div className="text-gray-800 mt-0.5">{indentData.indentNo || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <label className="text-gray-500 font-medium">Created</label>
                                            <div className="text-gray-800 mt-0.5">
                                                {indentData.createdAt
                                                    ? moment(indentData.createdAt).format('DD MMM YYYY, hh:mm A')
                                                    : 'N/A'}
                                            </div>
                                        </div>
                                        {indentData.updatedAt && (
                                            <div>
                                                <label className="text-gray-500 font-medium">Last Updated</label>
                                                <div className="text-gray-600 mt-0.5">
                                                    {moment(indentData.updatedAt).format('DD MMM, hh:mm A')}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Items List */}
                                    {items.length > 0 && (
                                        <div>
                                            <label className="text-xs font-medium text-gray-500 mb-2 block">
                                                Items ({itemsCount})
                                            </label>
                                            <div className="space-y-1.5">
                                                {items.map((item, itemIndex) => {
                                                    const statusIcon = getItemStatusIcon(item.status);
                                                    return (
                                                        <div
                                                            key={itemIndex}
                                                            className="flex items-center justify-between p-2 bg-white rounded text-xs"
                                                        >
                                                            <div className="flex-1 min-w-0">
                                                                <div className="font-medium text-gray-800 truncate">
                                                                    Item ID: {item.itemId || 'Unknown Medicine'}
                                                                </div>
                                                                <div className="text-gray-600 text-xs mt-0.5">
                                                                    Qty: {item.quantity || 0}
                                                                </div>
                                                                <div className="text-gray-600 text-xs mt-0.5">
                                                                    Remarks : {item.lineItemRemarks}
                                                                </div>
                                                            </div>
                                                            <span className="ml-2 text-gray-600 whitespace-nowrap">
                                                                {statusIcon} {item.status || indentData.status || 'SENT'}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Notes */}
                                    {indentData.notes && (
                                        <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                                            <label className="font-medium text-blue-800 mb-1 block">Notes</label>
                                            <div className="text-blue-900">{indentData.notes}</div>
                                        </div>
                                    )}

                                    {/* Cancel Button (if pending) */}
                                    {indentData.status === 'PENDING' && (
                                        <button
                                            className="w-full px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded border border-red-200 transition-colors"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // TODO: Implement cancel functionality
                                                console.log('Cancel indent transfer', indentData.indentNo);
                                            }}
                                        >
                                            Cancel Transfer
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default IndentTransferCard;